import { gql } from '@apollo/client'

export const CHAT_DETAILS_OFFER_FRAGMENT = gql`
  fragment ChatDetailsOfferFragment on Offer {
    id
    postID
    mcNumber
    emptyAtPreviousReceiverTime
    location
    amount {
      value
      currency
    }
    connection {
      carrierContractType
      connectionWorkspaceID
      numberOfFalloffs
      numberOfTrips
      ownerWorkspaceID
      status
    }
    message
    user {
      email
      phone
      id
      name
      role
      username
      photo
      primaryMembership {
        accessType
        isPrimary
        workspace {
          displayName
          logo
          isVerified
        }
      }
    }
    workspace {
      displayName
      logo
      isVerified
      dot
      company {
        id
        companyName
        mcNumber
        allowedToOperate
        authorizedForProperty
        authorizedForBroker
        brokerAuthorityStatus
        commonAuthorityStatus
        contractAuthorityStatus
        phoneNumber
        emailAddress
        faxNumber
        carrierOperation
        isHazmat
        numberOfPowerUnits
        numberOfDrivers
        mcsData {
          mcisAddDate
        }
      }
    }
    created
    isLastMessageRespondedTo
    isLastMessageSentByPostOwner
    lastMessageDateTime
    lastMessageSentByUserID
    lastMessageSentByUser {
      id
      name
      email
      phone
      photo
    }
    lastMessageText
  }
`

export const POST_STOPS_FRAGMENT = gql`
  fragment PostStopFields on PostStop {
    id
    warehouse {
      name
      category
      rating
      reviews
      placeID
      thumbnail
      warehouseHours
      address {
        addressLabel
        streetNumber
        street
        city
        state
        stateCode
        county
        postalCode
        postalCodeSuffix
        country
        countryCode
        coordinates {
          latitude
          longitude
        }
      }
    }
    appointment {
      start {
        date
        time
      }
      end {
        date
        time
      }
      isFirstComeFirstServe
    }
    referenceNumber
    type
    sequence
    instructions
  }
`

export const PUBLIC_POST_FRAGMENT = gql`
  fragment PostFields on Post {
    commodity
    commodityLineItems {
      description
      handlingUnitType
      numberOfHandlingUnits
      totalHeight {
        unit
        value
      }
      totalLength {
        unit
        value
      }
      totalWeight {
        unit
        value
      }
      totalWidth {
        unit
        value
      }
    }
    description
    destination {
      streetNumber
      street
      city
      state
      stateCode
      county
      postalCode
      postalCodeSuffix
      country
      countryCode
      addressLabel
      coordinates {
        latitude
        longitude
      }
    }
    destinationAppointment {
      start {
        date
        time
        timezone
      }
      end {
        date
        time
        timezone
      }
      isFirstComeFirstServe
    }
    distance {
      unit
      value
    }
    equipment {
      additionalRequirements
    }
    equipmentTags
    equipmentLength {
      unit
      value
    }
    id
    isHazmat
    isTeam
    isTempControlled
    loadNumber
    numberOfPicks
    numberOfDrops
    origin {
      streetNumber
      street
      city
      state
      stateCode
      county
      postalCode
      postalCodeSuffix
      country
      countryCode
      addressLabel
      coordinates {
        latitude
        longitude
      }
    }
    originAppointment {
      start {
        date
        time
        timezone
      }
      end {
        date
        time
        timezone
      }
      isFirstComeFirstServe
    }
    temperatureRange {
      unit
      min
      max
      runType
    }
    equipmentTags
    truckType
    postStatus
    weight {
      value
      unit
    }
    workspace {
      id
      workspacename
      displayName
      logo
      isVerified
    }
    stops {
      ...PostStopFields
    }
    isPostOwner
  }
  ${POST_STOPS_FRAGMENT}
`

export const POST_WITH_RATES_FRAGMENT = gql`
  fragment PostWithRatesFragment on Post {
    ...PostFields
    distance {
      unit
      value
    }
    tmsURL
    customer {
      name
    }
    tmsRates {
      source
      confidenceLevel
      benchmarkRate {
        currency
        value
      }
      offerRate {
        currency
        value
      }
      minRate {
        currency
        value
      }
      maxRate {
        currency
        value
      }
      startRate {
        currency
        value
      }
      targetRate {
        currency
        value
      }
      updatedAt
    }
    greenscreensRates {
      source
      confidenceLevel
      benchmarkRate {
        currency
        value
      }
      offerRate {
        currency
        value
      }
      minRate {
        currency
        value
      }
      maxRate {
        currency
        value
      }
      startRate {
        currency
        value
      }
      targetRate {
        currency
        value
      }
      updatedAt
    }
    created
  }
  ${PUBLIC_POST_FRAGMENT}
`

export const TEAM_MEMBER_FRAGMENT = gql`
  fragment TeamMemberFragment on User {
    id
    name
    username
    email
    phone
    photo
    emailOrPhone
  }
`

export const DISPATCHER_FRAGMENT = gql`
  fragment ExistingDispatchers on Shipment {
    dispatchers {
      user {
        id
      }
      workspace {
        id
      }
    }
  }
`

export const DRIVER_FRAGMENT = gql`
  fragment ExistingDrivers on Shipment {
    drivers {
      user {
        id
      }
      workspace {
        id
      }
    }
  }
`

export const SHIPMENT_FRAGMENT = gql`
  fragment MyShipment on Shipment {
    id
    dispatchers {
      __typename
    }
    drivers {
      __typename
    }
  }
`

export const OFFER_FRAGMENT = gql`
  fragment OfferFragment on Offer {
    id
    postID
    mcNumber
    dotNumber
    emptyAtPreviousReceiverTime
    location
    amount {
      value
      currency
    }
    message
    user {
      id
      name
      role
      username
      photo
      primaryMembership {
        accessType
        isPrimary
        workspace {
          displayName
          logo
          isVerified
        }
      }
    }
    offerStatus
    created
  }
`

export const RATES_FRAGMENT = gql`
  fragment RatesFields on Rates {
    source
    sourceDashboardURL
    confidenceLevel
    benchmarkRate {
      currency
      value
    }
    offerRate {
      currency
      value
    }
    minRate {
      currency
      value
    }
    maxRate {
      currency
      value
    }
    startRate {
      currency
      value
    }
    targetRate {
      currency
      value
    }
    updatedAt
  }
`

export const FINANCIALS_FRAGMENT = gql`
  fragment FinancialsFields on Financials {
    customerQuoteTotal
    customerChargeTotal
  }
`

export const PRIVATE_POST_OFFER_FRAGMENT = gql`
  fragment PrivatePostOfferFragment on Offer {
    id
    postID
    mcNumber
    dotNumber
    emptyAtPreviousReceiverTime
    location
    amount {
      value
      currency
    }
    connection {
      carrierContractType
      connectionWorkspaceID
      numberOfFalloffs
      numberOfTrips
      ownerWorkspaceID
      status
    }
    message
    user {
      id
      name
      role
      username
      photo
      primaryMembership {
        accessType
        isPrimary
        workspace {
          displayName
          logo
          isVerified
          id
          workspaceIPMetadata {
            hasUsedAbusiveIP
            hasUsedBogonIP
            hasUsedCrawler
            hasUsedDatacenterIP
            hasUsedProxy
            hasUsedTOR
            hasUsedVPN
            id
            ipLocations
            lastUsedIP {
              ip
              isAbuser
              isBogon
              isCrawler
              isDatacenter
              isProxy
              isTOR
              isVPN
              lastUsed
              location {
                city
                country
                countryCode
                region
                state
                latitude
                longitude
                zip
              }
            }
          }
        }
      }
      userIPMetadata {
        hasUsedAbusiveIP
        hasUsedBogonIP
        hasUsedCrawler
        hasUsedDatacenterIP
        hasUsedProxy
        hasUsedTOR
        hasUsedVPN
        id
        ipLocations
        lastUsedIP {
          ip
          isAbuser
          isBogon
          isCrawler
          isDatacenter
          isProxy
          isTOR
          isVPN
          lastUsed
          location {
            city
            country
            countryCode
            region
            state
            latitude
            longitude
            zip
          }
        }
      }
      userIPs {
        ip
        isAbuser
        isBogon
        isCrawler
        isDatacenter
        isProxy
        isTOR
        isVPN
        lastUsed
        location {
          city
          country
          countryCode
          region
          state
          latitude
          longitude
          zip
        }
      }
    }
    workspace {
      displayName
      logo
      isVerified
      id
      workspaceIPMetadata {
        hasUsedAbusiveIP
        hasUsedBogonIP
        hasUsedCrawler
        hasUsedDatacenterIP
        hasUsedProxy
        hasUsedTOR
        hasUsedVPN
        id
        ipLocations
        lastUsedIP {
          ip
          isAbuser
          isBogon
          isCrawler
          isDatacenter
          isProxy
          isTOR
          isVPN
          lastUsed
          location {
            city
            country
            countryCode
            region
            state
            latitude
            longitude
            zip
          }
        }
      }
    }
    offerStatus
    created
    isFromBannedCarrier
  }
`
